import { useCallback, useEffect, useRef, useState } from 'react'
import { Form, Formik } from 'formik'
import { FileText, Home } from 'lucide-react'
import { Link, useLocation, useParams } from 'react-router-dom'
import { PageLink, PageTitle } from '../../../_theme/layout/core'
import { Content } from '../../../_theme/layout/components/Content'
import { ManageProjectStageToolbar } from '../../../_theme/layout/components/toolbar/ManageProjectStageToolbar'
import { LeftStepperProvider } from '../../../_theme/context/LeftStepperContext'
import { useSnackbar } from '../../../_theme/context/SnackbarContext'
import { useAuth } from '../../modules/auth'
import Loader from '../../modules/apps/components/Loader'
import NotFound from '../../modules/apps/components/NotFound'
import { ElevatorPitch } from '../../modules/project/components/ElevatorPitch/ElevatorPitch'
import { getelevator_pitch } from '../../modules/project'
import { IElevatorPitch, IElevatorPitchStakeholder } from '../../modules/core/_models'
import { buildNavigationState, envConfig, getDashboardLink, getDashboardStagegates, getProjectStageLink } from '../../../utils/config'
import { getApiErrorMessage, isForbiddenError } from '../../../utils/utils'
import { isAbortedError } from '../../../utils/global'

const emptyElevatorPitch: IElevatorPitch = {
    updated_background_justification: '',
    background_justification: '',
    key_objectives_summary_editor: '',
    project_objectives_areas: [],
    project_status: '',
    financial_needs: {
        project_cost: '',
        project_type: '',
        project_size: '',
        project_cost_editor: '',
    },
    private_sector_opportunities: '',
    identity_key_stakeholders: {
        government: [],
        state_owned: [],
        private_sector: [],
        civil_society: [],
        other_stakeholders: [],
    },
    timeline: {
        estimated_project_duration_years: '',
        estimated_project_duration_months: '',
        estimated_project_start_date: '',
        timeline_table_areas: [],
    },
}

export const ElevatorPitchPage = () => {
    const { project_id } = useParams()
    const location = useLocation()
    const { showSnackbar } = useSnackbar()
    const { auth } = useAuth()
    const user_id = auth?.user_id
    const isApprover = auth?.user_details?.is_approver === 'Yes'

    const [initialValues, setInitialValues] = useState<IElevatorPitch>(emptyElevatorPitch)
    const [pageLoader, setPageLoader] = useState(true)
    const [projectName, setProjectName] = useState('')
    const [stakeholders, setStakeholders] = useState<IElevatorPitchStakeholder[]>([])
    const [accessDeniedMessage, setAccessDeniedMessage] = useState('')
    const abortRef = useRef<AbortController | null>(null)

    const applyElevatorPitch = useCallback((data: Awaited<ReturnType<typeof getelevator_pitch>>['data']) => {
        setProjectName(data.elevator_pitch_data?.project_name || '')
        setStakeholders(data.identity_key_stakeholders || [])
        setInitialValues({
            updated_background_justification: data.elevator_pitch_data?.elevator_project_overview || '',
            background_justification: data.elevator_pitch_data?.project_overview || '',
            key_objectives_summary_editor: data.elevator_pitch_data?.key_objectives_summary_editor || '',
            project_objectives_areas: data.objectives || [],
            project_status: data.elevator_pitch_data?.project_status || '',
            financial_needs: {
                project_cost: data.elevator_pitch_data?.estimated_cost || '',
                project_type: data.elevator_pitch_data?.project_type || '',
                project_size: data.elevator_pitch_data?.project_size || '',
                project_cost_editor: data.elevator_pitch_data?.financial_needs || '',
            },
            private_sector_opportunities: data.elevator_pitch_data?.private_sector_opportunities || '',
            identity_key_stakeholders: emptyElevatorPitch.identity_key_stakeholders,
            timeline: {
                estimated_project_duration_years: data.elevator_pitch_data?.estimated_years != null
                    ? String(data.elevator_pitch_data.estimated_years)
                    : '',
                estimated_project_duration_months: data.elevator_pitch_data?.estimated_months != null
                    ? String(data.elevator_pitch_data.estimated_months)
                    : '',
                estimated_project_start_date: data.elevator_pitch_data?.estimated_project_start_date || '',
                timeline_table_areas: data.elevator_pitch_timeline || [],
            },
        })
    }, [])

    useEffect(() => {
        if (!project_id) {
            setPageLoader(false)
            return
        }

        abortRef.current?.abort()
        const controller = new AbortController()
        abortRef.current = controller

        const fetchData = async () => {
            try {
                setPageLoader(true)
                setAccessDeniedMessage('')
                const pitchResponse = await getelevator_pitch({ project_id, user_id }, controller.signal)
                if (controller.signal.aborted) return

                if (pitchResponse.errorCode === 'FORBIDDEN') {
                    setAccessDeniedMessage(pitchResponse.message || 'Access denied. Elevator pitch is available only after Stage 4 is completed.')
                } else if (pitchResponse.status === 'ok' && pitchResponse.data && !Array.isArray(pitchResponse.data)) {
                    applyElevatorPitch(pitchResponse.data)
                } else {
                    showSnackbar(pitchResponse.message || 'Failed to fetch data', 'danger')
                }
            } catch (error) {
                if (isAbortedError(error)) return
                if (isForbiddenError(error)) {
                    setAccessDeniedMessage(getApiErrorMessage(error, 'Access denied. Elevator pitch is available only after Stage 4 is completed.'))
                    return
                }
                showSnackbar(getApiErrorMessage(error, 'Something went wrong while fetching data'), 'danger')
            } finally {
                if (!controller.signal.aborted) setPageLoader(false)
            }
        }

        fetchData()
        return () => abortRef.current?.abort()
    }, [applyElevatorPitch, project_id, showSnackbar, user_id])

    useEffect(() => {
        document.body.classList.add('bg-custom-gray')
        return () => {
            document.body.classList.remove('bg-custom-gray')
        }
    }, [])

    if (pageLoader) return <Loader loadingText="Loading projects…" />
    if (!project_id) return <NotFound />
    if (accessDeniedMessage) {
        return (
            <div
                className="d-flex flex-column flex-center"
                style={{ height: 'calc(100vh - var(--bs-app-header-height, 70px) - 4.5rem)' }}
            >
                <NotFound notFoundTitle="Access denied" notFoundText={accessDeniedMessage} homeButtonText="Return to Project Stages" homeButtonLink={`${getProjectStageLink(project_id)}${location.search}`} />
            </div>
        )
    }

    const breadCrumbs: Array<PageLink> = isApprover
        ? [
            {
                title: 'My Pending Requests',
                path: getDashboardStagegates(),
                isSeparator: false,
                isActive: false,
            },
            {
                title: '',
                path: '',
                isSeparator: true,
                isActive: false,
            },
            {
                title: 'Draft Elevator Pitch',
                path: '',
                isSeparator: false,
                isActive: true,
            },
        ]
        : [
            {
                title: 'Home',
                path: getDashboardLink(),
                isSeparator: false,
                isActive: false,
                icon: <Home size={18} />,
            },
            {
                title: '',
                path: '',
                isSeparator: true,
                isActive: false,
            },
            {
                title: 'My Projects',
                path: getDashboardStagegates(),
                isSeparator: false,
                isActive: false,
            },
            {
                title: '',
                path: '',
                isSeparator: true,
                isActive: false,
            },
            {
                title: 'Project Stages',
                path: `${getProjectStageLink(project_id)}${location.search}`,
                isSeparator: false,
                isActive: false,
                state: buildNavigationState(location.state?.from),
            },
            {
                title: '',
                path: '',
                isSeparator: true,
                isActive: false,
            },
            {
                title: 'Draft Elevator Pitch',
                path: '',
                isSeparator: false,
                isActive: true,
            },
        ]

    return (
        <div className="position-relative">
            <PageTitle sitetitle={envConfig.APP_GOV_TITLE} breadcrumbs={breadCrumbs}>
                {projectName || 'Draft Elevator Pitch'}
            </PageTitle>
            <ManageProjectStageToolbar
                rightSection={
                    <Link
                        to={`/manage-project/single-page-report/${project_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-lg btn-primary fs-3"
                    >
                        <FileText className="text-white me-2" size={24} />
                        Report
                    </Link>
                }
            />
            <Content>
                <LeftStepperProvider>
                    <Formik enableReinitialize initialValues={initialValues} onSubmit={() => undefined}>
                        {({ values }) => {
                            return (
                                <Form className="w-100">
                                    <ElevatorPitch
                                        values={values}
                                        stakeholders={stakeholders}
                                        submitLoader={false}
                                        disableFormFields={isApprover}
                                    />
                                </Form>
                            )
                        }}
                    </Formik>
                </LeftStepperProvider>
            </Content>
        </div>
    )
}
